import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, Plus, Warning, ShieldWarning, EnvelopeSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { AccessDenied } from "@/components/AccessDenied";
import { useSortable, SortTh } from "@/components/SortableTable";
import { Checkbox } from "@/components/ui/checkbox";
import HeatChip from "@/components/HeatChip";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

const BULK_DELETE_CAP = 50;

// Machine-readable skip reasons the backend can return → operator-facing text.
const SKIP_REASON_LABELS = {
  LINKED_QUOTE: "has a linked quote",
  LINKED_PROJECT: "has a linked project",
  PROTECTED_BUSINESS_RECORD: "has a protected business record",
  NOT_FOUND: "no longer exists",
};
function humanizeSkipReasons(reasons) {
  if (!reasons || reasons.length === 0) return "protected record";
  return reasons.map(r => SKIP_REASON_LABELS[r] || r).join(", ");
}

const LEAD_CATEGORIES = [
  "Initial Contact", "Demo Scheduled", "Proposal Sent",
  "Negotiation", "Closed Won", "Closed Lost", "On Hold",
];

const CAT_COLORS = {
  "Initial Contact": "bg-blue-50 text-blue-700",
  "Demo Scheduled": "bg-purple-50 text-purple-700",
  "Proposal Sent": "bg-amber-50 text-amber-700",
  "Negotiation": "bg-orange-50 text-orange-700",
  "Closed Won": "bg-emerald-50 text-emerald-700",
  "Closed Lost": "bg-red-50 text-red-700",
  "On Hold": "bg-[#F5F5F5] text-[#525252]",
};

const SOURCES = ["LinkedIn", "Referral", "Website", "Cold Outreach", "Conference", "Partner", "Other"];

// Canonical list of product modules shown on the public Quote Request
// form at eyvo.com. Keep in sync with WebsiteQuoteIn.modules_interested
// on the backend and with the marketing site's checkbox list.
export const LEAD_MODULES = [
  "Requisition",
  "Purchase Order",
  "Receiving",
  "Invoice",
  "Inventory",
  "Asset Registration",
  "Realtime Supplier Credit/Risk Check",
  "Contract Management",
  "Expense Management",
  "Payments",
  "RFQ/RFP/RFI",
  "Recipe",
  "Supplier Portal",
  "Customer Portal",
  "Third-party API Integration",
];

// v1.67 — Friendly labels for the tooltips on the Paused / Inactive
// ─── Canonical marketing state ──────────────────────────────────────
//
// This column used to be a binary: `is_enrolled` (derived from
// `e.status = 'active'`) or the words "NOT ENROLLED". That put
// deliberately held, authoritatively suppressed, archived and
// long-completed records into the same bucket as genuinely enrollable
// ones — roughly 1,800 held/historical leads reading as if they were
// campaign work waiting to be done.
//
// NOT ENROLLED must never imply SHOULD BE ENROLLED. The backend now
// resolves one canonical `marketing_state`; this renders it.
//
// Styling is deliberately restrained: only SUPPRESSED carries a warning
// tone, because it is the only state where contacting the person would
// be wrong. HELD is calm and neutral — it is a decision already taken,
// not an error the operator must fix.
const MARKETING_STATE_BADGES = {
  READY_FOR_CAMPAIGN: {
    label: "Ready for campaign",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
    title: "Genuinely enrollable: live, reachable, unsuppressed, unheld.",
  },
  ENROLLED: {
    label: "Enrolled",
    className: "bg-blue-50 text-blue-700 border-blue-200",
    title: "Currently in an active marketing workflow.",
  },
  HELD: {
    label: "Held",
    className: "bg-neutral-100 text-neutral-600 border-neutral-300",
    title:
      "Deliberately excluded from marketing — reputation, provenance, " +
      "quality or future-nurture hold. No action needed.",
  },
  SUPPRESSED: {
    label: "Suppressed",
    className: "bg-amber-50 text-amber-800 border-amber-300",
    title: "Must not receive marketing — authoritative suppression.",
  },
  HISTORICAL: {
    label: "Historical",
    className: "bg-neutral-50 text-neutral-500 border-neutral-200",
    title:
      "Completed or cancelled a prior journey. A new campaign is a " +
      "separate business decision, not an oversight.",
  },
  ARCHIVED: {
    label: "Archived",
    className: "bg-neutral-50 text-neutral-400 border-neutral-200",
    title: "Removed from active CRM/marketing use by operator cleanup.",
  },
  ERRORED: {
    label: "Errored",
    className: "bg-orange-50 text-orange-700 border-orange-200",
    title: "A prior enrolment errored and is awaiting investigation.",
  },
  INELIGIBLE: {
    label: "Not eligible",
    className: "bg-neutral-50 text-neutral-500 border-neutral-200",
    title:
      "Inactive, quarantined, no usable email, or a terminal lead status.",
  },
};

function MarketingStateBadge({ state, isEnrolled }) {
  // Fall back to the legacy binary only if the backend gave us nothing.
  const key = state || (isEnrolled ? "ENROLLED" : null);
  const b = MARKETING_STATE_BADGES[key];
  if (!b) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#F5F5F5] text-[#A3A3A3] border border-[#E5E5E5] w-fit">
        Unknown
      </span>
    );
  }
  return (
    <span
      title={b.title}
      data-testid={`marketing-state-${key}`}
      className={
        "inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase " +
        "tracking-wider border w-fit " + b.className
      }
    >
      {b.label}
    </span>
  );
}

// badges in the leads list. Used by the table cell renderer so an
// operator can hover and immediately see why a lead is in that state.
const PAUSE_REASON_LABELS = {
  "invalid_email":               "email bounced",
  "invalid_email_inferred":      "email bounced (inferred from historical data)",
  "unsubscribed":                "lead unsubscribed",
  "unsubscribed_inferred":       "lead unsubscribed (inferred)",
  "booking_click":               "booked a meeting via drip CTA",
  "booking_click_inferred":      "booked a meeting (inferred)",
  "engagement_reply":            "lead replied to a drip email",
  "do_not_contact_inferred":     "manually flagged 'do not contact'",
  "orphan_cleanup":              "stopped during orphan cleanup",
  "historical_unknown_inferred": "paused before tracking — reason unknown",
};
export function humanizePauseReason(raw) {
  if (!raw) return null;
  if (PAUSE_REASON_LABELS[raw]) return PAUSE_REASON_LABELS[raw];
  // Manual sales reasons land here verbatim (e.g. "Do not contact —
  // flagged by sales"). Show them as-is.
  return raw;
}
export function humanizeLeadStatus(raw) {
  if (!raw) return null;
  // Pass-through; the status labels are already operator-friendly.
  return raw.charAt(0).toUpperCase() + raw.slice(1).replace(/_/g, " ");
}


function NewLeadModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: "", contact_person: "", contact_email: "", phone_number: "",
    lead_category: "Initial Contact", lead_type: "Active", source_of_lead: "",
    ball_with: "Eyvo", assigned_to: "", registration_notes: "",
    modules_interested: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleModule = (m) => setForm(p => ({
    ...p,
    modules_interested: p.modules_interested.includes(m)
      ? p.modules_interested.filter(x => x !== m)
      : [...p.modules_interested, m],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_person) { setError("Company name and contact person required"); return; }
    setSaving(true); setError("");
    try {
      const res = await api.post("/crm/leads", form);
      onSaved(res.data);
    } catch (err) { setError(err.response?.data?.detail || "Failed to create lead"); }
    setSaving(false);
  };

  const inputCls = "w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF] transition-colors";

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" data-testid="new-lead-modal">
      <div className="bg-white shadow-xl w-full max-w-xl border border-[#E5E5E5]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
          <h2 className="font-bold text-[#0A0A0A] text-sm uppercase tracking-wider">New Sales Lead</h2>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-[#0A0A0A] text-lg">x</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Company <span className="text-red-500">*</span></label>
              <input value={form.company_name} onChange={e => set("company_name", e.target.value)} className={inputCls} data-testid="new-lead-company" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Contact <span className="text-red-500">*</span></label>
              <input value={form.contact_person} onChange={e => set("contact_person", e.target.value)} className={inputCls} data-testid="new-lead-contact" />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Email</label>
              <input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Phone</label>
              <input value={form.phone_number} onChange={e => set("phone_number", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Category</label>
              <select value={form.lead_category} onChange={e => set("lead_category", e.target.value)} className={inputCls}>
                {LEAD_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Source</label>
              <select value={form.source_of_lead} onChange={e => set("source_of_lead", e.target.value)} className={inputCls}>
                <option value="">Select source</option>
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Assigned To</label>
              <input value={form.assigned_to} onChange={e => set("assigned_to", e.target.value)} placeholder="e.g. Mike" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Ball With</label>
              <select value={form.ball_with} onChange={e => set("ball_with", e.target.value)} className={inputCls}>
                <option value="Eyvo">Eyvo</option><option value="Client">Client</option><option value="Both">Both</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-[#525252] mb-1 uppercase tracking-wider">Notes</label>
            <textarea value={form.registration_notes} onChange={e => set("registration_notes", e.target.value)} rows={2} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#525252] mb-1.5 uppercase tracking-wider">Modules Interested In</label>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 border border-[#E5E5E5] p-3 bg-[#FAFAFA]" data-testid="new-lead-modules">
              {LEAD_MODULES.map(m => (
                <label key={m} className="flex items-center gap-2 text-xs text-[#0A0A0A] cursor-pointer select-none hover:bg-white px-1 py-0.5">
                  <input
                    type="checkbox"
                    checked={form.modules_interested.includes(m)}
                    onChange={() => toggleModule(m)}
                    className="w-3.5 h-3.5 accent-[#0055FF]"
                    data-testid={`module-cb-${m.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                  />
                  <span>{m}</span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-[#A3A3A3] mt-1">Matches the "Modules Interested In?" checkboxes on the public Quote Request form.</p>
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2">{error}</div>}
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving} className="px-4 py-2 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#003ECB] disabled:opacity-50" data-testid="save-new-lead-btn">
              {saving ? "Saving..." : "Create Lead"}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-bold border border-[#E5E5E5] hover:bg-[#F5F5F5] uppercase tracking-wider">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function LeadsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canBulkDelete = user?.role === "platform_admin";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [marketingState, setMarketingState] = useState("");
  const [category, setCategory] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  // Lost leads (Closed Lost / Closed Won) are noise on the active
  // pipeline view. Hidden by default; a checkbox flips this on so the
  // historical view is one click away.
  const [includeLost, setIncludeLost] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [quarantineCount, setQuarantineCount] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    api.get("/crm/pending-leads?status=pending")
      .then(r => setPendingCount((r.data || []).length))
      .catch(() => {});
    api.get("/crm/leads/quarantine")
      .then(r => setQuarantineCount((r.data || []).length))
      .catch(() => {});
  }, []);

  const fetchLeads = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    if (activeFilter !== "") params.set("is_active", activeFilter);
    if (includeLost) params.set("include_lost", "true");
    // Server-side, because a filter has to narrow the whole result set,
    // not just what is already loaded.
    if (marketingState) params.set("marketing_state", marketingState);
    try {
      const res = await api.get(`/crm/leads?${params}`);
      // Flatten heat for SortableTable + format last action label.
      const enriched = (res.data || []).map(l => ({
        ...l,
        heat_rank: l.heat?.days == null ? 99999 : l.heat.days,
        // engagement_score = opens + 3*clicks (clicks are stronger signal)
        // so a lead who clicked once outranks one with 2 opens.
        engagement_score: (l.engagement_opens || 0) + 3 * (l.engagement_clicks || 0),
      }));
      setLeads(enriched);
    } catch (e) { if (e.response?.status === 403) setAccessDenied(true); else console.error(e); }
    setLoading(false);
  };

  const [heatSummary, setHeatSummary] = useState(null);
  const [heatFilter, setHeatFilter] = useState(null);
  useEffect(() => {
    api.get("/crm/leads/heat-summary")
      .then(r => setHeatSummary(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchLeads(); },
    [search, category, activeFilter, includeLost, marketingState]);

  const { sorted, sortKey, sortDir, onSort } = useSortable(leads, "heat_rank", "asc");
  const visible = heatFilter ? sorted.filter(l => (l.heat?.band || "cold") === heatFilter) : sorted;

  // Selection is scoped to the current filtered/search result set (`visible`)
  // so a lead outside the operator's current view/tenant filter can never be
  // swept up by "select all". Clear whenever the underlying filters change so
  // a stale selection can't silently carry over to a different result set.
  useEffect(() => { setSelected(new Set()); },
    [search, category, activeFilter, includeLost, heatFilter, marketingState]);

  const toggleSelected = (leadId) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(leadId)) n.delete(leadId); else n.add(leadId);
      return n;
    });
  };
  const allVisibleSelected = visible.length > 0 && visible.every(l => selected.has(l.lead_id));
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) { setSelected(new Set()); return; }
    setSelected(new Set(visible.map(l => l.lead_id)));
  };

  const bulkDeleteSelected = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const permanent = "This permanently deletes each lead and its notes, meetings, and activity history. " +
      "Leads with a linked quote or project are automatically skipped and left untouched. This cannot be undone.";
    if (!window.confirm(`Delete ${ids.length} lead${ids.length === 1 ? "" : "s"}?\n\n${permanent}`)) return;
    const big = ids.length > BULK_DELETE_CAP;
    if (big && !window.confirm(
      `You're about to delete ${ids.length} leads at once — more than the usual ${BULK_DELETE_CAP}-lead batch. Continue?`
    )) return;
    setBulkDeleting(true);
    try {
      const res = await api.post("/crm/leads/bulk-delete", {
        lead_ids: ids, confirm_large_batch: big,
      });
      const d = res.data;
      const skippedResults = (d.results || []).filter(r => r.status === "skipped");
      toast.success(`Deleted ${d.deleted} · skipped ${d.skipped} · failed ${d.failed}`, {
        description: skippedResults.length
          ? `Skipped: ${skippedResults.slice(0, 3).map(r => `${r.lead_id} (${humanizeSkipReasons(r.reasons)})`).join("; ")}`
            + (skippedResults.length > 3 ? ` and ${skippedResults.length - 3} more` : "")
          : undefined,
      });
      setSelected(new Set());
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  // Pause / resume from the row — mirrors LeadDetail.togglePause but works
  // off the table row data. Optimistically updates local state so the badge
  // flips instantly without waiting for a re-fetch round-trip.
  const togglePause = async (lead, isCurrentlyPaused) => {
    if (isCurrentlyPaused) {
      if (!window.confirm(`Resume "${lead.company_name}"?\nFuture chase emails will be allowed again.`)) return;
      try {
        await api.post(`/crm/leads/${lead.lead_id}/resume`);
        setLeads(prev => prev.map(l => l.lead_id === lead.lead_id ? { ...l, is_paused: false, pause_reason: null } : l));
        toast.success(`Resumed "${lead.company_name}"`);
      } catch (e) { toast.error(e.response?.data?.detail || "Resume failed"); }
      return;
    }
    const reason = window.prompt(
      `Pause "${lead.company_name}" — no chase emails will go out until resumed.\nOptional reason:`,
      "Do not contact — flagged by sales"
    );
    if (reason === null) return;
    try {
      await api.post(`/crm/leads/${lead.lead_id}/pause`, { reason: reason || "Manual pause" });
      setLeads(prev => prev.map(l => l.lead_id === lead.lead_id ? { ...l, is_paused: true, pause_reason: reason || "Manual pause" } : l));
      toast.success(`Paused "${lead.company_name}" — outbound emails suppressed.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Pause failed"); }
  };

  if (accessDenied) return <AccessDenied module="Sales Leads" />;

  return (
    <div className="p-6" data-testid="leads-list-page">
      {showModal && <NewLeadModal onClose={() => setShowModal(false)} onSaved={(lead) => { setShowModal(false); navigate(`/crm/leads/${lead.lead_id}`); }} />}

      <div className="flex items-center justify-start gap-6 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight">Sales Leads</h1>
          <p className="text-sm text-[#A3A3A3] mt-0.5">{leads.length} lead{leads.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#003ECB] transition-colors" data-testid="new-lead-button">
          <Plus size={15} weight="bold" /> New Lead
        </button>
        <button
          onClick={() => navigate("/crm/pending-leads")}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${pendingCount > 0 ? "bg-[#FEF2F2] text-[#DC2626] border border-[#DC2626] hover:bg-[#FEE2E2]" : "bg-white text-[#525252] border border-[#E5E5E5] hover:bg-[#FAFAFA]"}`}
          data-testid="pending-leads-button"
        >
          <Warning size={15} weight="bold" />
          Pending Review
          {pendingCount > 0 && <span className="px-1.5 py-0.5 bg-[#DC2626] text-white text-[10px] rounded-full min-w-[18px] text-center">{pendingCount}</span>}
        </button>
        <button
          onClick={() => navigate("/crm/leads/quarantine")}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${quarantineCount > 0 ? "bg-[#FEF3C7] text-[#92400E] border border-[#D97706] hover:bg-[#FDE68A]" : "bg-white text-[#525252] border border-[#E5E5E5] hover:bg-[#FAFAFA]"}`}
          data-testid="quarantine-button"
        >
          <ShieldWarning size={15} weight="bold" />
          Quarantine
          {quarantineCount > 0 && <span className="px-1.5 py-0.5 bg-[#D97706] text-white text-[10px] rounded-full min-w-[18px] text-center">{quarantineCount}</span>}
        </button>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
          <input type="text" placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="leads-search-input" />
        </div>
        <select value={category} onChange={e => setCategory(e.target.value)}
          className="border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]" data-testid="leads-category-filter">
          <option value="">All Categories</option>
          {LEAD_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          {/* Session 88 pt9 — virtual filter, not a real lead_category.
              Backend treats the sentinel `__not_enrolled__` as a JOIN
              against mkt_workflow_enrollments. Surfaces leads we can
              bulk-enrol from /admin/marketing/enrol-leads. */}
          {/* The old "— Not Enrolled —" sentinel filtered on
              `no active enrolment`, which surfaced ~1,800 deliberately
              held and historical records as if they were campaign work.
              Marketing state now has its own filter below. */}
        </select>

        {/* Marketing state — the canonical states, so filtering agrees
            with the badges. "Ready for campaign" is the only value that
            means there is work to do. */}
        <select
          data-testid="marketing-state-filter"
          value={marketingState}
          onChange={(e) => setMarketingState(e.target.value)}
          className="border border-[#E5E5E5] px-3 py-2 text-sm bg-white"
        >
          <option value="">Marketing: all states</option>
          <option value="READY_FOR_CAMPAIGN">Ready for campaign</option>
          <option value="ENROLLED">Enrolled</option>
          <option value="HELD">Held</option>
          <option value="SUPPRESSED">Suppressed</option>
          <option value="HISTORICAL">Historical</option>
          <option value="ARCHIVED">Archived</option>
          <option value="ERRORED">Errored</option>
          <option value="INELIGIBLE">Not eligible</option>
        </select>
        <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)}
          className="border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]" data-testid="leads-active-filter">
          <option value="">All</option><option value="true">Active</option><option value="false">Inactive</option>
        </select>
        {/* Session 67 — Lost leads are excluded by default. Operators
            opt-in to the historical view via this checkbox. */}
        <label
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-[#525252] border border-[#E5E5E5] cursor-pointer hover:bg-[#FAFAFA] select-none"
          data-testid="leads-include-lost-label"
        >
          <input
            type="checkbox"
            checked={includeLost}
            onChange={e => setIncludeLost(e.target.checked)}
            className="accent-[#0055FF]"
            data-testid="leads-include-lost-checkbox"
          />
          <span className="font-bold uppercase tracking-wider text-[10px]">
            Include Lost
          </span>
        </label>
      </div>

      {canBulkDelete && selected.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 mb-4 px-4 py-2.5 bg-[#0A0A0A] text-white"
          role="toolbar"
          aria-label="Bulk actions"
          data-testid="leads-bulk-toolbar"
        >
          <span className="text-sm font-semibold" aria-live="polite" data-testid="leads-selected-count">
            {selected.size} lead{selected.size === 1 ? "" : "s"} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#D4D4D4] hover:text-white"
              data-testid="leads-clear-selection-btn"
            >
              Clear
            </button>
            <button
              onClick={bulkDeleteSelected}
              disabled={bulkDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
              data-testid="leads-bulk-delete-btn"
            >
              <Trash size={13} weight="bold" />
              {bulkDeleting ? "Deleting…" : `Delete Selected (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      {heatSummary && heatSummary.total > 0 && (
        <HeatMapWidget summary={heatSummary} filter={heatFilter} onFilter={setHeatFilter} />
      )}

      <div className="bg-white border border-[#E5E5E5] overflow-hidden">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
              {canBulkDelete && (
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={toggleSelectAllVisible}
                    aria-label={allVisibleSelected ? "Clear selection" : "Select all visible leads"}
                    data-testid="leads-select-all"
                  />
                </th>
              )}
              <SortTh sortKey="heat_rank" label="Heat" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="lead_ref" label="Ref" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="company_name" label="Company" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="contact_person" label="Contact" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="lead_category" label="Category" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="ball_with" label="Ball With" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="source_of_lead" label="Source" current={sortKey} dir={sortDir} onSort={onSort} />
              {/* ENTERED — when the lead FIRST entered the Pulse lead
                  population (`entry_date`, falling back to `created_at`).
                  Distinct from Last Activity: a 2022 lead emailed
                  yesterday is recent activity but an old entry. Click to
                  sort; the list is unpaginated so this orders the whole
                  filtered set. */}
              <SortTh sortKey="entered_at" label="Entered" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="last_action_date" label="Last Activity" current={sortKey} dir={sortDir} onSort={onSort} />
              {/* Session 67 — surfaces (a) workflow enrolment yes/no and
                  (b) the next email's scheduled send date for that lead.
                  Sortable on `next_scheduled_at` so a sales rep can see
                  who's about to be touched next without opening each
                  lead individually. */}
              <SortTh sortKey="next_scheduled_at" label="Next Activity" current={sortKey} dir={sortDir} onSort={onSort} />
              {/* Session 67 — engagement (opens + clicks) so reps spot
                  hot leads without drilling into the timeline. */}
              <SortTh sortKey="engagement_score" label="Engaged" current={sortKey} dir={sortDir} onSort={onSort} />
              <SortTh sortKey="is_active" label="Status" current={sortKey} dir={sortDir} onSort={onSort} />
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.16em] text-[#525252]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F5F5F5]">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <tr key={i}>{[...Array(canBulkDelete ? 13 : 12)].map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-[#F5F5F5] animate-pulse" /></td>)}</tr>
              ))
            ) : visible.length === 0 ? (
              <tr><td colSpan={canBulkDelete ? 13 : 12} className="px-4 py-12 text-center text-[#A3A3A3]">No leads found</td></tr>
            ) : (
              visible.map(lead => (
                <tr key={lead.lead_id} className={`hover:bg-[#FAFAFA] cursor-pointer transition-colors ${lead.is_paused ? "bg-amber-50/40" : ""} ${selected.has(lead.lead_id) ? "bg-blue-50/40" : ""}`}
                    onClick={() => navigate(`/crm/leads/${lead.lead_id}?edit=1`)} data-testid={`lead-row-${lead.lead_id}`}>
                  {canBulkDelete && (
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(lead.lead_id)}
                        onCheckedChange={() => toggleSelected(lead.lead_id)}
                        aria-label={`Select ${lead.company_name || "lead"}`}
                        data-testid={`lead-select-${lead.lead_id}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <HeatChip heat={lead.heat} />
                  </td>
                  <td className="px-2 py-3">
                    {lead.lead_ref ? (
                      <span
                        className="font-mono text-[10px] tracking-tight text-[#A3A3A3] whitespace-nowrap"
                        data-testid={`lead-ref-${lead.lead_id}`}
                        title={lead.lead_ref}
                      >
                        {/* Display the numeric portion only; full ref is
                            in the title for accessibility. Saves ~40px
                            of horizontal real estate that the new
                            Next Activity / Engaged columns need. */}
                        {(lead.lead_ref || "").replace(/^L-/, "")}
                      </span>
                    ) : (
                      <span className="text-[#D4D4D4] text-[10px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-[#0A0A0A]">{lead.company_name}</p>
                    {lead.deletion_requested && <span className="text-xs text-red-500">Deletion requested</span>}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-[#525252]">{lead.contact_person}</p>
                    {lead.contact_email && <p className="text-xs text-[#A3A3A3]">{lead.contact_email}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-bold ${CAT_COLORS[lead.lead_category] || "bg-[#F5F5F5] text-[#525252]"}`}>
                      {lead.lead_category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#525252] text-xs">{lead.ball_with || "-"}</td>
                  <td className="px-4 py-3 text-[#A3A3A3] text-xs">{lead.source_of_lead || "-"}</td>
                  <td
                    className="px-4 py-3 text-[#525252] text-xs font-mono whitespace-nowrap"
                    data-testid={`lead-entered-${lead.lead_id}`}
                    title="When this lead first entered the Pulse lead population"
                  >
                    {lead.entered_at ? formatLocalDate(lead.entered_at) : "—"}
                  </td>
                  <td className="px-4 py-3 text-[#A3A3A3] text-xs font-mono" data-testid={`lead-last-${lead.lead_id}`}>
                    {lead.last_action_date ? formatLocalDate(lead.last_action_date) : "—"}
                  </td>
                  {/* Next Activity column — Session 67 */}
                  <td className="px-4 py-3" data-testid={`lead-next-${lead.lead_id}`}>
                    <div className="flex flex-col gap-0.5">
                      <MarketingStateBadge
                        state={lead.marketing_state}
                        isEnrolled={lead.is_enrolled}
                      />
                      {lead.is_enrolled && (
                        <span className="text-xs text-[#525252] font-mono whitespace-nowrap">
                          {lead.next_scheduled_at ? formatLocalDate(lead.next_scheduled_at) : "—"}
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Engagement column — Session 67. Tiny eye / cursor
                      glyphs keep the row dense; full counts in title. */}
                  <td className="px-3 py-3" data-testid={`lead-engagement-${lead.lead_id}`}>
                    {(lead.engagement_opens > 0 || lead.engagement_clicks > 0) ? (
                      <div className="flex items-center gap-2 text-xs">
                        {lead.engagement_opens > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[#525252]"
                            title={`${lead.engagement_opens} email open${lead.engagement_opens === 1 ? "" : "s"}`}
                          >
                            👁 <span className="font-mono">{lead.engagement_opens}</span>
                          </span>
                        )}
                        {lead.engagement_clicks > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 font-bold text-emerald-700"
                            title={`${lead.engagement_clicks} link click${lead.engagement_clicks === 1 ? "" : "s"}`}
                          >
                            ↗ <span className="font-mono">{lead.engagement_clicks}</span>
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-[#D4D4D4] text-[10px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {lead.is_paused ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 cursor-help"
                        title={
                          `Paused — ${humanizePauseReason(lead.pause_reason) || "no recorded reason"}` +
                          (lead.last_action_date
                            ? `\nLast action: ${lead.last_action_date.slice(0, 10)}`
                            : "")
                        }
                      >
                        Paused
                      </span>
                    ) : (
                      <span
                        className={`px-2 py-0.5 text-xs font-bold cursor-help ${lead.is_active ? "bg-emerald-50 text-emerald-700" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}
                        title={
                          lead.is_active
                            ? "Active — eligible for drip emails"
                            : `Inactive — ${humanizeLeadStatus(lead.lead_status) || "marked inactive"}` +
                              (lead.last_action_date
                                ? `\nLast action: ${lead.last_action_date.slice(0, 10)}`
                                : "")
                        }
                      >
                        {lead.is_active ? "Active" : "Inactive"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5">
                      {lead.bcc_token && (
                        <button
                          onClick={async () => {
                            const addr = `bcc-${lead.bcc_token}@replies.eyvopulse.com`;
                            try {
                              await navigator.clipboard.writeText(addr);
                              toast.success(`BCC for ${lead.company_name} copied`);
                            } catch {
                              toast.error("Could not copy — clipboard blocked");
                            }
                          }}
                          className="p-1 text-[#525252] hover:text-[#D97706] hover:bg-[#FFFBEB] transition-colors flex-shrink-0"
                          data-testid={`row-copy-bcc-${lead.lead_id}`}
                          title="Copy BCC archive email for this lead — paste into the Bcc field of your next outbound email to archive it under Externally Sent Emails."
                        >
                          <EnvelopeSimple size={14} />
                        </button>
                      )}
                      {lead.is_paused ? (
                      <button
                        onClick={() => togglePause(lead, true)}
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]"
                        data-testid={`row-resume-${lead.lead_id}`}
                        title="Resume — allow emails again"
                      >
                        ▶ Resume
                      </button>
                    ) : (
                      <button
                        onClick={() => togglePause(lead, false)}
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-[#D97706] text-[#D97706] hover:bg-[#FFFBEB]"
                        data-testid={`row-pause-${lead.lead_id}`}
                        title="Pause — stop chase emails for this lead"
                      >
                        ⏸ Pause
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ───────── Heat-map widget ───────── */
const BAND_META = {
  hot:  { label: "Hot",  hint: "≤10 days", color: "#DC2626" },
  warm: { label: "Warm", hint: "11–21 days", color: "#D97706" },
  cool: { label: "Cool", hint: "22–60 days", color: "#0D9488" },
  cold: { label: "Cold", hint: "60+ days",   color: "#2563EB" },
};

function HeatMapWidget({ summary, filter, onFilter }) {
  const total = Math.max(1, summary.total || 0);
  const bands = ["hot", "warm", "cool", "cold"];
  return (
    <div className="bg-white border border-[#E5E5E5] p-4 mb-4" data-testid="heat-map-widget">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Pipeline heat</p>
          <p className="text-xs text-[#525252] mt-0.5">
            {summary.total} active lead{summary.total === 1 ? "" : "s"}
            {filter && <span> · filtered to <b>{BAND_META[filter].label}</b></span>}
          </p>
        </div>
        {filter && (
          <button onClick={() => onFilter(null)}
                  className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] hover:underline"
                  data-testid="heat-filter-clear">
            Clear filter
          </button>
        )}
      </div>
      {/* Stacked bar */}
      <div className="flex h-8 border border-[#E5E5E5] overflow-hidden" data-testid="heat-bar">
        {bands.map(b => {
          const count = summary[b] || 0;
          const pct = (count / total) * 100;
          const meta = BAND_META[b];
          if (count === 0) return null;
          const active = filter === b;
          return (
            <button
              key={b}
              onClick={() => onFilter(active ? null : b)}
              title={`${meta.label} · ${count} lead${count === 1 ? "" : "s"} · ${meta.hint}`}
              className="transition-opacity flex items-center justify-center text-white text-[11px] font-bold tabular-nums"
              style={{
                width: `${pct}%`,
                backgroundColor: meta.color,
                opacity: filter && !active ? 0.35 : 1,
                borderRight: "1px solid rgba(255,255,255,0.3)",
              }}
              data-testid={`heat-band-${b}`}
            >
              {pct > 8 ? count : ""}
            </button>
          );
        })}
      </div>
      {/* Legend */}
      <div className="grid grid-cols-4 gap-2 mt-2">
        {bands.map(b => {
          const meta = BAND_META[b];
          const count = summary[b] || 0;
          const active = filter === b;
          return (
            <button
              key={b}
              onClick={() => onFilter(active ? null : b)}
              className={`flex items-center gap-2 px-2 py-1 text-left transition-colors ${active ? "bg-[#F5F5F5]" : "hover:bg-[#FAFAFA]"}`}
              data-testid={`heat-legend-${b}`}
            >
              <span className="w-2 h-2 flex-shrink-0" style={{ backgroundColor: meta.color }} />
              <span className="text-[11px] font-bold text-[#0A0A0A]">{meta.label}</span>
              <span className="text-[11px] text-[#525252] tabular-nums">{count}</span>
              <span className="text-[10px] text-[#A3A3A3] ml-auto hidden md:inline">{meta.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
